import { useEffect, useMemo, useState } from 'react';
import { FileDown } from 'lucide-react';
import { Modale } from '../ui/Modale';
import { BoutonMint, BoutonSecondaire, Chargement, MessageErreur } from '../ui/Primitives';
import { usePerimetre } from '../../site/usePerimetre';
import { telechargerFeuilleAudit, useSessionsAudit } from '../../lib/useEmargement';

/** Un mois au plus : la règle est celle de l'API, rappelée ici pour guider la saisie. */
const JOURS_MAX = 31;

function decaler(iso: string, jours: number): string {
  const [annee, mois, jour] = iso.split('-').map(Number);
  return new Date(Date.UTC(annee!, mois! - 1, jour! + jours)).toISOString().slice(0, 10);
}

/** Lundi et dimanche de la semaine d'un jour `AAAA-MM-JJ`. */
function semaineDe(iso: string): { du: string; au: string } {
  const [annee, mois, jour] = iso.split('-').map(Number);
  const rang = new Date(Date.UTC(annee!, mois! - 1, jour!)).getUTCDay();
  const du = decaler(iso, -((rang + 6) % 7));
  return { du, au: decaler(du, 6) };
}

function ecart(du: string, au: string): number {
  return Math.round((Date.parse(au) - Date.parse(du)) / 86_400_000) + 1;
}

function dateCourte(iso: string): string {
  const [annee, mois, jour] = iso.split('-');
  return `${jour}/${mois}/${annee}`;
}

/**
 * « Export PDF audit » (CDC §6.7) : la feuille d'émargement d'une session sur
 * une période, une page par semaine — apprenants en lignes, demi-journées en
 * colonnes, signatures dans les cases et celle du formateur en pied. C'est la
 * feuille collective que la tablette remplace, telle qu'un auditeur la demande.
 */
export function ModaleExportAudit({
  ouverte,
  jour,
  onFermer,
}: {
  ouverte: boolean;
  /** Journée affichée à l'écran : la semaine proposée par défaut est la sienne. */
  jour: string;
  onFermer: () => void;
}): JSX.Element {
  const { site } = usePerimetre();
  const sessions = useSessionsAudit(site, ouverte);
  const [sessionId, setSessionId] = useState('');
  const [periode, setPeriode] = useState(() => semaineDe(jour));
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    if (!ouverte) return;
    setPeriode(semaineDe(jour));
    setErreur(null);
  }, [ouverte, jour]);

  const liste = useMemo(() => sessions.data?.sessions ?? [], [sessions.data]);
  useEffect(() => {
    if (!liste.some((session) => session.sessionId === sessionId)) {
      setSessionId(liste[0]?.sessionId ?? '');
    }
  }, [liste, sessionId]);

  const choisie = liste.find((session) => session.sessionId === sessionId) ?? null;
  const refus =
    periode.au < periode.du
      ? 'La fin de la période doit suivre son début.'
      : ecart(periode.du, periode.au) > JOURS_MAX
        ? `La feuille couvre ${JOURS_MAX} jours au plus : exportez la période mois par mois.`
        : null;

  async function exporter(): Promise<void> {
    if (!choisie || refus) return;
    setEnCours(true);
    setErreur(null);
    try {
      await telechargerFeuilleAudit(choisie.sessionId, periode.du, periode.au);
    } catch (cause) {
      setErreur(cause instanceof Error ? cause.message : 'La feuille n’a pas pu être produite.');
    } finally {
      setEnCours(false);
    }
  }

  const champ =
    'min-h-[44px] w-full min-w-0 rounded-xl border border-gray-200 bg-white px-3 text-sm text-codialis-navy';

  return (
    <Modale
      ouverte={ouverte}
      titre="Export PDF audit"
      sousTitre="Feuille d’émargement d’une session, une page par semaine, signatures comprises"
      onFermer={onFermer}
    >
      <div className="space-y-4 p-4 sm:p-6">
        {sessions.isLoading && <Chargement libelle="Chargement des sessions…" />}
        {sessions.error !== null && <MessageErreur message="Les sessions n’ont pas pu être chargées." />}
        {sessions.data && liste.length === 0 && (
          <p className="text-sm text-codialis-gray">
            Aucune session de ce périmètre n’a encore de feuille d’émargement en salle.
          </p>
        )}

        {liste.length > 0 && (
          <>
            <label className="block text-sm font-semibold text-codialis-navy">
              Session
              <select
                value={sessionId}
                onChange={(evenement) => setSessionId(evenement.target.value)}
                className={`${champ} mt-1.5 font-normal`}
              >
                {liste.map((session) => (
                  <option key={session.sessionId} value={session.sessionId}>
                    {session.siteCode} · {session.session} · {session.formationCode}
                  </option>
                ))}
              </select>
            </label>
            {choisie && (
              <p className="text-xs text-codialis-gray">
                {choisie.seances} demi-journée{choisie.seances > 1 ? 's' : ''} émargée
                {choisie.seances > 1 ? 's' : ''} du {dateCourte(choisie.premiereSeance)} au{' '}
                {dateCourte(choisie.derniereSeance)}.
              </p>
            )}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="block min-w-0 text-sm font-semibold text-codialis-navy">
                Du
                <input
                  type="date"
                  value={periode.du}
                  onChange={(evenement) => setPeriode((avant) => ({ ...avant, du: evenement.target.value }))}
                  className={`${champ} mt-1.5 font-normal`}
                />
              </label>
              <label className="block min-w-0 text-sm font-semibold text-codialis-navy">
                Au
                <input
                  type="date"
                  value={periode.au}
                  onChange={(evenement) => setPeriode((avant) => ({ ...avant, au: evenement.target.value }))}
                  className={`${champ} mt-1.5 font-normal`}
                />
              </label>
            </div>
            <div className="flex flex-wrap gap-2">
              <BoutonSecondaire onClick={() => setPeriode(semaineDe(jour))}>Semaine affichée</BoutonSecondaire>
              <BoutonSecondaire
                onClick={() => {
                  const du = `${jour.slice(0, 8)}01`;
                  const [annee, mois] = jour.split('-').map(Number);
                  const dernier = new Date(Date.UTC(annee!, mois!, 0)).getUTCDate();
                  setPeriode({ du, au: `${jour.slice(0, 8)}${String(dernier).padStart(2, '0')}` });
                }}
              >
                Mois affiché
              </BoutonSecondaire>
            </div>

            {refus && <MessageErreur message={refus} />}
            {erreur && <MessageErreur message={erreur} />}

            <p className="text-xs text-codialis-gray">
              Une feuille encore ouverte y figure comme telle : seule la feuille clôturée fait foi,
              et son PV reste la pièce de la demi-journée. Chaque export est tracé.
            </p>
          </>
        )}

        <div className="flex flex-wrap gap-2">
          <BoutonMint onClick={() => void exporter()} disabled={!choisie || refus !== null || enCours}>
            <FileDown className="mr-1.5 h-4 w-4" aria-hidden="true" />
            {enCours ? 'Production…' : 'Télécharger la feuille'}
          </BoutonMint>
          <BoutonSecondaire onClick={onFermer}>Fermer</BoutonSecondaire>
        </div>
      </div>
    </Modale>
  );
}
